import React, { useMemo } from 'react'
import { emojify } from 'node-emoji'
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  styled,
} from '@mui/material'
import { IconTrash, IconPlus } from '@tabler/icons-react'
import {
  CampaignSetupFormValues,
  ContactQuestion,
  ContextCardStyle,
  QuestionType,
  QUESTION_TYPE_LABELS,
} from '../types'
import StepNavigation from './StepNavigation'

const SectionCard = styled(Card)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  boxShadow: 'none',
  marginBottom: theme.spacing(3),
}))

const SubSectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '0.95rem',
  marginBottom: theme.spacing(1.5),
  marginTop: theme.spacing(2.5),
  color: theme.palette.secondary.dark,
}))

const QuestionRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 0),
  borderBottom: `1px solid ${theme.palette.divider}`,
}))

const CONTEXT_CARD_STYLES: { value: ContextCardStyle; label: string }[] = [
  { value: 'PARAGRAPH_STYLE', label: 'Paragraph Style' },
  { value: 'LIST_STYLE', label: 'List Style' },
]

const ALL_QUESTION_TYPES: QuestionType[] = [
  'FULL_NAME',
  'EMAIL',
  'PHONE_NUMBER',
  'CITY',
  'STATE',
  'JOB_TITLE',
  'COMPANY_NAME',
  'JOB_ROLE',
  'CUSTOM',
]

const labelToKey = (label: string) =>
  label
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')

interface Props {
  values: CampaignSetupFormValues
  onChange: (field: keyof CampaignSetupFormValues, value: unknown) => void
  onNext: () => void
  onBack: () => void
  readOnly?: boolean
}

const LeadFormStep: React.FC<Props> = ({ values, onChange, onNext, onBack, readOnly }) => {
  const emojifiedDescription = useMemo(
    () => (values.introDescription ? emojify(values.introDescription) : ''),
    [values.introDescription]
  )

  const ojAutoFill = () => {
    onChange(
      'questionPageCustomHeadline',
      'Wir verwenden Ihre Informationen, um Sie bezüglich der Stelle zu kontaktieren.'
    )
    onChange('privacyPolicyUrl', 'https://onlyjobs.de/datenschutz')
    onChange('thankYouTitle', 'Vielen Dank für Ihre Bewerbung!')
    onChange(
      'thankYouBody',
      'Wir freuen uns Sie kennenzulernen. Wir melden und in Kürze bei Ihnen.'
    )
    onChange('thankYouButtonText', 'Jetzt mehr erfahren')
    onChange('contactQuestions', [
      { id: `q_${Date.now()}_1`, type: 'FULL_NAME', required: true },
      { id: `q_${Date.now()}_2`, type: 'EMAIL', required: true },
      { id: `q_${Date.now()}_3`, type: 'PHONE_NUMBER', required: true },
      {
        id: `q_${Date.now()}_4`,
        type: 'CUSTOM',
        label: 'Was ist Dein/Ihr aktueller Beruf oder alternativ Ausbildungsberuf/Studienfach?',
        key: 'aktueller_beruf',
        required: false,
      },
    ])
  }

  const addQuestion = () => {
    const newQuestion: ContactQuestion = {
      id: `q_${Date.now()}`,
      type: 'FULL_NAME',
      required: false,
    }
    onChange('contactQuestions', [...values.contactQuestions, newQuestion])
  }

  const updateQuestion = (id: string, field: keyof ContactQuestion, value: unknown) => {
    const extra =
      field === 'type' && value === 'CUSTOM'
        ? { isCustom: true, label: '', key: '' }
        : field === 'type'
        ? { isCustom: false }
        : {}
    onChange(
      'contactQuestions',
      values.contactQuestions.map((q) => (q.id === id ? { ...q, [field]: value, ...extra } : q))
    )
  }

  const updateCustomLabel = (id: string, label: string) => {
    const key = labelToKey(label)
    onChange(
      'contactQuestions',
      values.contactQuestions.map((q) => (q.id === id ? { ...q, label, key } : q))
    )
  }

  const removeQuestion = (id: string) => {
    onChange(
      'contactQuestions',
      values.contactQuestions.filter((q) => q.id !== id)
    )
  }

  return (
    <Box>
      <SectionCard>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Lead Form
            </Typography>
            <Chip label="FIND Invitation Page" size="small" color="primary" variant="outlined" />
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={ojAutoFill}
              sx={{ ml: 'auto' }}
              disabled={readOnly}
            >
              OJ Auto Fill
            </Button>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Name *"
                value={values.leadFormName}
                onChange={(e) => onChange('leadFormName', e.target.value)}
                placeholder="Lead Form - Sales Manager Berlin"
                disabled={readOnly}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Privacy Policy URL *"
                value={values.privacyPolicyUrl}
                onChange={(e) => onChange('privacyPolicyUrl', e.target.value)}
                placeholder="https://example.com/privacy"
                helperText="Required — please enter a valid privacy policy URL"
                disabled={readOnly}
              />
            </Grid>
          </Grid>

          {/* Intro */}
          <SubSectionTitle>Intro</SubSectionTitle>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Welcome text shown to users before the questions.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ position: 'relative', display: 'inline-flex', width: '100%' }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Headline"
                  value={values.introHeadline}
                  onChange={(e) => onChange('introHeadline', e.target.value)}
                  placeholder="Apply in 2 minutes"
                  disabled={readOnly}
                />
                {values.introHeadline.length > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -10,
                      right: -10,
                      minWidth: 22,
                      height: 22,
                      borderRadius: '50%',
                      bgcolor: values.introHeadline.length > 60 ? 'error.main' : 'grey.400',
                      color: '#fff',
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 0.5,
                      pointerEvents: 'none',
                      lineHeight: 1,
                    }}
                  >
                    {values.introHeadline.length > 60
                      ? values.introHeadline.length
                      : 60 - values.introHeadline.length}
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={3}
                label="Description"
                value={values.introDescription}
                onChange={(e) => onChange('introDescription', e.target.value)}
                placeholder="Fill out the short form and we will get back to you."
                inputProps={{ style: { resize: 'vertical' } }}
                disabled={readOnly}
              />
              {emojifiedDescription && emojifiedDescription !== values.introDescription && (
                <Box
                  sx={{
                    mt: 0.5,
                    px: 1.5,
                    py: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    fontSize: '0.875rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    color: 'text.secondary',
                  }}
                >
                  {emojifiedDescription}
                </Box>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" disabled={readOnly}>
                <InputLabel>Context Card Style</InputLabel>
                <Select
                  value={values.contextCardStyle}
                  label="Context Card Style"
                  onChange={(e) => onChange('contextCardStyle', e.target.value as ContextCardStyle)}
                >
                  {CONTEXT_CARD_STYLES.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Contact Information */}
          <SubSectionTitle>Contact Information</SubSectionTitle>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Let people know how the information they give you will be used or shared.
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Description"
                value={values.questionPageCustomHeadline}
                onChange={(e) => onChange('questionPageCustomHeadline', e.target.value)}
                placeholder="We will only use your information to contact you about this position."
                disabled={readOnly}
              />
            </Grid>
          </Grid>

          {/* Contact Questions */}
          <SubSectionTitle>Questions</SubSectionTitle>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Predefined types (FULL_NAME, EMAIL, PHONE_NUMBER, ...) or Custom Questions
          </Typography>

          {values.contactQuestions.map((q, index) => (
            <QuestionRow key={q.id}>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 20 }}>
                {index + 1}
              </Typography>

              {q.isCustom ? (
                <TextField
                  size="small"
                  label="Label"
                  value={q.label || ''}
                  onChange={(e) => updateCustomLabel(q.id, e.target.value)}
                  sx={{ flex: 3 }}
                  disabled={readOnly}
                />
              ) : (
                <Box sx={{ flex: 3 }}>
                  <Typography variant="body2">{QUESTION_TYPE_LABELS[q.type] || q.type}</Typography>
                </Box>
              )}

              <FormControl size="small" sx={{ flex: 1 }} disabled={readOnly}>
                <Select
                  value={q.type}
                  onChange={(e) => updateQuestion(q.id, 'type', e.target.value as QuestionType)}
                >
                  {ALL_QUESTION_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      {QUESTION_TYPE_LABELS[t]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Tooltip title="Required field">
                <Checkbox
                  size="small"
                  checked={q.required}
                  onChange={(e) => updateQuestion(q.id, 'required', e.target.checked)}
                  disabled={readOnly}
                />
              </Tooltip>
              <Typography variant="caption" color="text.secondary">
                Required
              </Typography>
              <IconButton size="small" onClick={() => removeQuestion(q.id)} disabled={readOnly}>
                <IconTrash size={16} />
              </IconButton>
            </QuestionRow>
          ))}

          <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              startIcon={<IconPlus size={16} />}
              onClick={addQuestion}
              disabled={readOnly}
            >
              Add Question
            </Button>
          </Box>

          {/* Thank You Page */}
          <SubSectionTitle sx={{ mt: 3 }}>Thank You Page</SubSectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Title"
                value={values.thankYouTitle}
                onChange={(e) => onChange('thankYouTitle', e.target.value)}
                placeholder="Thank you!"
                disabled={readOnly}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Button Text"
                value={values.thankYouButtonText}
                onChange={(e) => onChange('thankYouButtonText', e.target.value)}
                placeholder="Visit Website"
                disabled={readOnly}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Button URL (optional)"
                value={values.thankYouButtonUrl}
                onChange={(e) => onChange('thankYouButtonUrl', e.target.value)}
                placeholder="https://example.com/business"
                disabled={readOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={3}
                label="Body"
                value={values.thankYouBody}
                onChange={(e) => onChange('thankYouBody', e.target.value)}
                placeholder="We have received your application and will get back to you shortly."
                disabled={readOnly}
              />
            </Grid>
          </Grid>
        </CardContent>
      </SectionCard>

      <StepNavigation onBack={onBack} onNext={onNext} />
    </Box>
  )
}

export default LeadFormStep
